import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";

import { toast } from "react-toastify";
import dayjs from "dayjs";
import ThemesTemplate from "../../templates/lookups/themes";
import {
  createThemeRequest,
  deleteThemeRequest,
  getThemeDetailsRequest,
  getThemesRequest,
  updateThemeRequest,
} from "../../services/modules/lookups/themes";

function ThemesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector((state) => state.themes.themes);
  const count = useSelector((state) => state.themes.count);
  const details = useSelector((state) => state.themes.theme);
  const load = useSelector((state) => state.themes.load);

  //permissions
  const currentUser = useSelector(
    (state) => state.authenticate.currentUserProfile
  );
  const isSuperAdmin = currentUser?.privilege == "super_admin";
  const can_create = isSuperAdmin;

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState({
    page_size: 10,
    page_number: 1,
    keyword: "",
    sort_by: "id",
  });

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  const getDetails = async () => {
    dispatch(getThemeDetailsRequest({ id: id }));
  };

  const getList = async (filterObj) => {
    const requestData = {
      params: filterObj,
    };
    dispatch(getThemesRequest(requestData));
  };

  const mappedData = list?.map((data, index) => {
    return {
      ...data,
      created_at: <>{dayjs(data?.created_at).format("DD-MM-YYYY HH:mm")}</>,
      image_url: data?.image_url ? (
        <img
          src={data?.image_url}
          alt={data?.name}
          width="125px"
          height="77px"
        />
      ) : (
        t("chat.no_image")
      ),
    };
  });

  const rowData = (data) => {
    setSelectedRow(data);
  };

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: !type ? false : true,
      path: "/lookups/themes",
      title: t("lookups_themes.title"),
    },
    type && {
      active: false,
      title: type === "details" ? t("lookups_themes.details") : id ? t("lookups_themes.edit") : t("lookups_themes.add"),
    },
  ];

  const handelSubmit = (data, formik) => {
    const action = () => {
      searchParams.delete("id");
      searchParams.delete("type");
      navigate("/lookups/themes");
      getList(filterObj);
      !id && formik?.resetForm();
      toast.success(
        id ? t("lookups_themes.updated") : t("lookups_themes.created")
      );
    };

    const requestData = {
      body: {
        theme: {
          image_url: data?.image,
          en: {
            name: data?.englishName,
            description: data?.englishDescription,
          },
          ar: {
            name: data?.arabicName,
            description: data?.arabicDescription,
          },
        },
      },
      action,
    };

    if (id) {
      dispatch(updateThemeRequest({ ...requestData, id }));
    } else {
      dispatch(createThemeRequest(requestData));
    }
  };

  const filterList = [];

  const handleDelete = (id) => {
    const action = () => {
      if (list.length === 1 && page !== 1) {
        setPage(page - 1);
        setFilterObj((prev) => ({
          ...prev,
          page_number: page - 1,
        }));
      } else {
        getList(filterObj);
      }
      type === "details" && navigate("/lookups/themes");
      toast.success(t("lookups_themes.deleted"));
    };
    const requestData = {
      id,
      action,
    };
    dispatch(deleteThemeRequest(requestData));
    setOpenDeleteModal(false);
  };

  const actionsList = ({ index, data }) => {
    return [
      {
        condition: () => {
          return isSuperAdmin;
        },
        click: () => {
          navigate(`?type=form&id=${data?.id}`);
        },
        text: t("common.edit"),
      },
      {
        condition: () => {
          return isSuperAdmin;
        },
        click: () => {
          setOpenDeleteModal(true);
        },
        text: t("common.delete"),
      },
    ];
  };

  const headerChildren = <></>;
  const modalComponents = [];

  useEffect(() => {
    if (filterObj) getList(filterObj);
  }, [filterObj]);

  useEffect(() => {
    if (id) getDetails();
  }, [id]);

  return (
    <>
      <Loader open={load?.loading && load?.type == "list"} />
      <ThemesTemplate
        filterObj={filterObj}
        setFilterObj={setFilterObj}
        data={mappedData}
        count={count}
        paginationPage={page}
        handlePageChange={handlePageChange}
        rowData={rowData}
        details={details}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        handelSubmit={handelSubmit}
        actionsList={actionsList}
        headerChildren={headerChildren}
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        handleDeleteRequest={handleDelete}
        modalComponents={modalComponents}
        can_create={can_create}
      />
    </>
  );
}

export default ThemesPage;
